import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { LayoutService } from '../../core/services/layout.service';
import { AuthService } from '../../core/services/auth.service';
import { StoreContextService } from '../../core/services/store-context.service';
import { BillingService } from '../../core/services/billing.service';
import { hasAnyPermission } from '../../core/utils/jwt.util';
import {
  LucideAngularModule,
  LayoutDashboard,
  Store as StoreIcon,
  Package,
  ShoppingCart,
  ChartBar,
  Users,
  Settings,
  ChevronDown,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  History,
  Shield,
  Clock,
  Wallet,
  FileText,
  ShoppingBag,
  SlidersHorizontal,
  Truck,
  PackagePlus,
  Banknote,
  TrendingUp,
} from 'lucide-angular';

const ADMIN_ROLES = ['SuperAdmin', 'Admin', 'Operator'];

/** Mijoz ilovasida ko'rinmasligi kerak bo'lgan modullar (kampaniya rollari) */
const SYSTEM_STAFF_ROLES = ADMIN_ROLES;

export interface NavItem {
  translateKey: string;
  href: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  /** Bo'sh bo'lsa yoki yo'q bo'lsa  faqat autentifikatsiya */
  permissions?: string[];
  customerOnly?: boolean;
  /** SuperAdmin, Admin, Operator uchun yashirish */
  hideForSystemStaff?: boolean;
  moduleKey?: string;
  /**
   * Guruh sarlavhasi kaliti. Ketma-ket bir xil `section`ga ega elementlar
   * bitta sarlavha ostida ko'rsatiladi (shablonda oldingi ko'rinadigan element
   * bilan solishtiriladi). Yo'q bo'lsa sarlavhasiz chiqadi — Bosh sahifa va
   * Sozlamalar guruhga kirmaydi, har doim mos ravishda eng boshda/oxirida turadi.
   */
  section?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, LucideAngularModule],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit {
  private layoutService = inject(LayoutService);
  private authService = inject(AuthService);
  private billingService = inject(BillingService);
  private readonly destroyRef = inject(DestroyRef);
  storeContext = inject(StoreContextService);

  isSidebarOpen = this.layoutService.isSidebarOpen;
  isSidebarCollapsed = this.layoutService.isSidebarCollapsed;
  includedModules = signal<string[] | null>(null);

  isAdminUser = computed(() => ADMIN_ROLES.includes(this.authService.user()?.role ?? ''));

  /** Do'kon tanlagich: faqat mijoz tenant ilovasi foydalanuvchilari */
  showStoreSelector = computed(
    () => !SYSTEM_STAFF_ROLES.includes(this.authService.user()?.role ?? ''),
  );

  Store = StoreIcon;
  Shield = Shield;
  Wallet = Wallet;
  FileText = FileText;
  ShoppingBag = ShoppingBag;
  ChevronDown = ChevronDown;
  X = X;
  PanelLeftClose = PanelLeftClose;
  PanelLeftOpen = PanelLeftOpen;

  navConfig: NavItem[] = [
    {
      translateKey: 'SIDEBAR.DASHBOARD',
      href: '/app',
      icon: LayoutDashboard,
      permissions: ['DASHBOARD_VIEW'],
      hideForSystemStaff: true,
    },

    // ── Katalog ──────────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.STORES',
      href: '/app/stores',
      icon: StoreIcon,
      permissions: ['STORE_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.CATALOG',
    },
    {
      translateKey: 'SIDEBAR.PRODUCTS',
      href: '/app/products',
      icon: Package,
      permissions: ['PRODUCT_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.CATALOG',
    },
    {
      translateKey: 'SIDEBAR.CATEGORIES',
      href: '/app/categories',
      icon: SlidersHorizontal,
      permissions: ['CATEGORY_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.CATALOG',
    },

    // ── Savdo ────────────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.SALES',
      href: '/app/sales',
      icon: ShoppingBag,
      permissions: ['ORDER_CREATE'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.SALES',
    },
    {
      translateKey: 'SIDEBAR.ORDERS',
      href: '/app/orders',
      icon: ShoppingCart,
      permissions: ['ORDER_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.SALES',
    },
    {
      translateKey: 'SIDEBAR.DEBTORS',
      href: '/app/debtors',
      icon: Wallet,
      permissions: ['ORDER_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.SALES',
    },

    // ── Ombor ────────────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.PURCHASES',
      href: '/app/purchases',
      icon: PackagePlus,
      permissions: ['PURCHASE_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.WAREHOUSE',
    },
    {
      translateKey: 'SIDEBAR.SUPPLIERS',
      href: '/app/suppliers',
      icon: Truck,
      permissions: ['SUPPLIER_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.WAREHOUSE',
    },
    {
      translateKey: 'SIDEBAR.WRITEOFF',
      href: '/app/write-off',
      icon: History,
      permissions: ['INVENTORY_WRITEOFF'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.WAREHOUSE',
    },

    // ── Pul ──────────────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.CASH',
      href: '/app/cash',
      icon: Banknote,
      permissions: ['FINANCE_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.MONEY',
    },
    {
      translateKey: 'SIDEBAR.FINANCE',
      href: '/app/finance',
      icon: TrendingUp,
      permissions: ['FINANCE_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.MONEY',
    },

    // ── Jamoa ────────────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.EMPLOYEES',
      href: '/app/employees',
      icon: Users,
      customerOnly: true,
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.TEAM',
    },
    {
      translateKey: 'SIDEBAR.ATTENDANCE',
      href: '/app/attendance',
      icon: Clock,
      permissions: ['ATTENDANCE_VIEW', 'ATTENDANCE_CLOCK', 'DASHBOARD_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.TEAM',
    },

    // ── Statistika ───────────────────────────────────────────────────────────
    {
      translateKey: 'SIDEBAR.ANALYTICS',
      href: '/app/analytics',
      icon: ChartBar,
      permissions: ['INVENTORY_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.INSIGHTS',
    },
    {
      translateKey: 'SIDEBAR.REPORTS',
      href: '/app/reports',
      icon: FileText,
      permissions: ['REPORT_VIEW'],
      hideForSystemStaff: true,
      section: 'SIDEBAR.SECTION.INSIGHTS',
    },

    { translateKey: 'SIDEBAR.SETTINGS', href: '/app/settings', icon: Settings },
  ];

  visibleNav = computed(() => {
    const role = this.authService.user()?.role;
    const token = this.authService.getAccessToken();
    const accountPermissions = this.authService.user()?.permissions;
    return this.navConfig.filter((item) => {
      if (item.hideForSystemStaff && role && SYSTEM_STAFF_ROLES.includes(role)) {
        return false;
      }
      if (item.customerOnly && role !== 'Customer') {
        return false;
      }
      if (item.moduleKey && !this.includedModules()?.includes(item.moduleKey)) {
        return false;
      }
      if (!item.permissions?.length) {
        return true;
      }
      return hasAnyPermission(token, role, item.permissions, accountPermissions);
    });
  });

  ngOnInit(): void {
    if (!SYSTEM_STAFF_ROLES.includes(this.authService.user()?.role ?? '')) {
      this.billingService
        .getMyBilling()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) =>
            this.includedModules.set(res.success ? (res.data.includedModules ?? []) : []),
          error: () => this.includedModules.set([]),
        });
    }
  }

  toggleSidebarCollapse() {
    this.layoutService.toggleSidebarCollapse();
  }

  closeSidebar() {
    this.layoutService.closeSidebar();
  }

  onStorePick(event: Event) {
    const v = (event.target as HTMLSelectElement).value;
    this.storeContext.setSelectedStoreId(v || null);
  }
}
